import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import type { PatientDiagnosis, UUID } from "@/core/types";
import { SHORT_LIVED_CACHE } from "@/lib/constants/cache";

import { getPatientDiagnosis } from "../queryFunctions/getPatientDiagnosis";
import { patientKeys } from "../keyFactory";

type UsePatientDiagnosisProps<TData = PatientDiagnosis> = {
  patientId: UUID;
  select?: (data: PatientDiagnosis) => TData;
};

export function usePatientDiagnosis<TData = PatientDiagnosis>({
  patientId,
  select,
}: UsePatientDiagnosisProps<TData>) {
  return useQuery({ ...optionFactory(patientId), select });
}

type PatientDiagnosisQueryOptions = UseQueryOptions<
  PatientDiagnosis,
  unknown,
  PatientDiagnosis,
  ReturnType<typeof patientKeys.diagnosis>
>;

// callers can use queryClient.prefetchQuery(optionFactory(patientId))
// or queryClient.getQueryData(optionFactory(id).queryKey) with correct typing
// without duplicating the key or queryFn
function optionFactory(patientId: UUID) {
  return {
    queryFn: () => getPatientDiagnosis(patientId),
    queryKey: patientKeys.diagnosis(patientId),
    staleTime: SHORT_LIVED_CACHE,
  } satisfies PatientDiagnosisQueryOptions;
}
